import { forwardRef, type ReactNode } from 'react';
import { TextInput, type TextInputProps } from '@mantine/core';
import { IconPhone } from '@tabler/icons-react';

/** Digits only, with a leading + kept. */
function clean(raw: string): string {
  const trimmed = raw.trim();
  const plus = trimmed.startsWith('+');
  const digits = trimmed.replace(/\D/g, '');
  return plus ? `+${digits}` : digits;
}

/**
 * South African cellphone and landline numbers → E.164 ("+27821234567").
 * Accepts "082 123 4567", "0821234567", "27821234567", "+27 82 123 4567" and "0027...".
 * Returns null when it isn't a valid SA number (other countries: use `normaliseAnyPhone`).
 */
export function toE164Sa(raw: string | null | undefined): string | null {
  if (!raw) return null;
  let n = clean(raw);
  if (n.startsWith('+27')) n = n.slice(3);
  else if (n.startsWith('0027')) n = n.slice(4);
  else if (n.startsWith('27') && n.length === 11) n = n.slice(2);
  else if (n.startsWith('0')) n = n.slice(1);
  else if (n.startsWith('+')) return null;
  if (!/^[1-8]\d{8}$/.test(n)) return null;
  return `+27${n}`;
}

/** True for a valid SA number (any of the formats toE164Sa accepts). */
export function isValidSaPhone(raw: string | null | undefined): boolean {
  return toE164Sa(raw) !== null;
}

/** "+27821234567" / "0821234567" → "082 123 4567". Anything else is returned trimmed. */
export function formatSaPhone(raw: string | null | undefined): string {
  const e164 = toE164Sa(raw);
  if (!e164) return (raw ?? '').trim();
  const local = `0${e164.slice(3)}`;
  return `${local.slice(0, 3)} ${local.slice(3, 6)} ${local.slice(6)}`;
}

/** Any number with at least 9 digits (international numbers allowed), cleaned. Null if too short. */
export function normaliseAnyPhone(raw: string | null | undefined): string | null {
  const sa = toE164Sa(raw);
  if (sa) return sa;
  const n = clean(raw ?? '');
  return n.replace(/\D/g, '').length >= 9 ? n : null;
}

export interface PhoneInputProps extends Omit<TextInputProps, 'value' | 'onChange' | 'type'> {
  value: string;
  /** Called while typing; on blur a valid SA number is tidied to "082 123 4567". */
  onChange: (value: string) => void;
  label?: ReactNode;
}

/**
 * Cellphone number field: phone keypad, autofill, a 48 px box and an example in the placeholder.
 * On blur a valid SA number is shown as "082 123 4567". Save it with toE164Sa() if the API
 * wants E.164, or as shown.
 */
export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(function PhoneInput(
  { value, onChange, label = 'Cellphone number', placeholder = '082 123 4567', onBlur, ...rest },
  ref,
) {
  return (
    <TextInput
      ref={ref}
      label={label}
      placeholder={placeholder}
      type="tel"
      inputMode="tel"
      autoComplete="tel"
      leftSection={<IconPhone size={18} aria-hidden="true" />}
      value={value}
      onChange={(e) => onChange(e.currentTarget.value)}
      onBlur={(e) => {
        if (isValidSaPhone(value)) onChange(formatSaPhone(value));
        onBlur?.(e);
      }}
      {...rest}
    />
  );
});
